import { useState } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { FileIcon, Pencil, Copy } from 'lucide-react'
import { type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import { DeleteIcon, MagicIcon } from '@/components/ui/icons/icons-new'
import { AiLabeledImage } from '../workspace-preview/ai-label-preview-overlay'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'

interface Props {
  media: WorkspaceMedia
  index: number
  onRemove: (index: number) => void
  onReorder: (fromIndex: number, toIndex: number) => void
  onEdit: () => void
  onDuplicate: (index: number) => void
  onAutoDesign?: (media: WorkspaceMedia, index: number) => void
  /** Draw the AI label over the media here too. Opt-in, because the workspace is
   *  where the artwork gets edited and a permanent overlay fights that view. */
  showAiLabel?: boolean
  aiLabelStyle?: AiLabelStyle
}

export function WorkspaceMediaPreview({
  media,
  index,
  onRemove,
  onReorder,
  onEdit,
  onDuplicate,
  onAutoDesign,
  showAiLabel = false,
  aiLabelStyle,
}: Props) {
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'))
    if (fromIndex !== index) {
      onReorder(fromIndex, index)
    }
  }

  const baseClassName = `relative group bg-transparent border rounded-lg aspect-square overflow-hidden cursor-move transition-all ${
    isDragOver ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50' : 'border-border'
  }`

  const renderActionButtons = () => {
    const showEdit = media.type === 'image' || media.type === 'canvas'

    return (
      <div className="absolute right-2 top-2 flex items-center gap-2 opacity-25 transition-opacity group-hover:opacity-100 max-md:opacity-100">
        {showEdit && (
          <Button variant="primary" size="icon-sm" onClick={() => onEdit()}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {onAutoDesign && (
          <Button variant="primary" size="icon-sm" onClick={() => onAutoDesign(media, index)}>
            <MagicIcon width={16} height={16} color="white" />
          </Button>
        )}
        <Button size="icon-sm" variant="secondary" onClick={() => onDuplicate(index)}>
          <Copy className="h-4 w-4" color="black" />
        </Button>
        <Button size="icon-sm" variant="secondary" onClick={() => onRemove(index)}>
          <DeleteIcon color="black" width={16} height={16} />
        </Button>
      </div>
    )
  }

  if (media.type === 'image') {
    return (
      <div
        // Centring lives here rather than on `baseClassName` because the
        // non-image branches share that class. The image is sized to the picture
        // (not the square tile) so the AI label anchors to the artwork instead
        // of the letterbox around it.
        className={`${baseClassName} flex items-center justify-center`}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <AiLabeledImage
          src={media.file.url}
          crossOrigin="anonymous"
          className="max-h-full max-w-full object-contain"
          showAiLabel={showAiLabel}
          aiLabelStyle={aiLabelStyle}
        />
        {renderActionButtons()}
      </div>
    )
  }

  if (media.type === 'video') {
    return (
      <div
        className={baseClassName}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <video
          src={media.file.url}
          autoPlay
          muted
          loop
          playsInline
          className="h-full w-full object-contain"
        />
        {renderActionButtons()}
      </div>
    )
  }

  if (media.type === 'document') {
    return (
      <div
        className={`${baseClassName} flex items-center justify-center`}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <div className="p-2 text-center">
          <FileIcon className="mx-auto mb-2 h-8 w-8 text-gray-600" />
          <div className="truncate text-xs font-medium">{media.file.url}</div>
        </div>
        {renderActionButtons()}
      </div>
    )
  }

  if (media.type === 'canvas') {
    return (
      <div
        // Centring lives here rather than on `baseClassName` because the
        // non-image branches share that class. The image is sized to the picture
        // (not the square tile) so the AI label anchors to the artwork instead
        // of the letterbox around it.
        className={`${baseClassName} flex items-center justify-center`}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <AiLabeledImage
          src={media.file.url}
          crossOrigin="anonymous"
          className="max-h-full max-w-full object-contain"
          showAiLabel={showAiLabel}
          aiLabelStyle={aiLabelStyle}
        />
        {renderActionButtons()}
      </div>
    )
  }

  return null
}
